import Markdown from 'react-markdown';
import type { PreviewKind } from './documentPreviewKind';
import './LiksDocumentPreviewBody.css';

export type LiksDocumentPreviewBodyProps = {
  isActiveReady: boolean;
  isLoading: boolean;
  isError: boolean;
  isInaccessible: boolean;
  isDownloadSuccess: boolean;
  previewKind: PreviewKind;
  blobText: string | null;
  objectUrl: string | null;
  displayTitle: string;
  loadingLabel: string;
  errorLabel: string;
  inaccessibleLabel?: string;
  unavailableLabel: string;
  notReadyLabel?: string;
};

export function LiksDocumentPreviewBody({
  isActiveReady,
  isLoading,
  isError,
  isInaccessible,
  isDownloadSuccess,
  previewKind,
  blobText,
  objectUrl,
  displayTitle,
  loadingLabel,
  errorLabel,
  inaccessibleLabel,
  unavailableLabel,
  notReadyLabel,
}: LiksDocumentPreviewBodyProps) {
  const loadingBody = (
    <div
      className="liks-document-preview__status"
      aria-busy="true"
      aria-label={loadingLabel}
    >
      <div className="liks-document-preview__spinner" aria-hidden />
      <span>{loadingLabel}</span>
    </div>
  );

  if (!isActiveReady) {
    return (
      <p className="liks-document-preview__status">
        {notReadyLabel ?? unavailableLabel}
      </p>
    );
  }

  if (isLoading && blobText == null && objectUrl == null) {
    return loadingBody;
  }

  if (isError) {
    return (
      <p className="liks-document-preview__status" role="alert">
        {isInaccessible ? (inaccessibleLabel ?? errorLabel) : errorLabel}
      </p>
    );
  }

  if (previewKind === 'markdown' && blobText != null) {
    return (
      <div className="liks-document-preview__markdown">
        <Markdown
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        >
          {blobText}
        </Markdown>
      </div>
    );
  }

  if (previewKind === 'text' && blobText != null) {
    return <pre className="liks-document-preview__text">{blobText}</pre>;
  }

  if (previewKind === 'image' && objectUrl) {
    return (
      <img
        className="liks-document-preview__img"
        src={objectUrl}
        alt={displayTitle}
      />
    );
  }

  if (previewKind === 'pdf' && objectUrl) {
    return (
      <iframe
        className="liks-document-preview__frame"
        src={objectUrl}
        title={displayTitle}
      />
    );
  }

  if (
    isDownloadSuccess &&
    (previewKind === 'markdown' || previewKind === 'text') &&
    blobText == null
  ) {
    return loadingBody;
  }

  if (isDownloadSuccess) {
    return <p className="liks-document-preview__status">{unavailableLabel}</p>;
  }

  return loadingBody;
}
